/*!
 * devextreme-angular-test
 * Version: 17.2.8
 * Build date: Mon Feb 05 2018
 *
 * Copyright (c) 2012 - 2018 Developer Express Inc. ALL RIGHTS RESERVED
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file in the root of the project for details.
 *
 * https://github.com/DevExpress/devextreme-angular
 */











import {
    Component,
    NgModule,
    ElementRef,
    NgZone,
    Input,
    Output,
    OnDestroy,
    Injector,
    EventEmitter,
    OnInit,
    AfterViewInit,
    ContentChild,
    forwardRef,
    HostListener,
    OnChanges,
    DoCheck,
    SimpleChanges,
    ContentChildren,
    QueryList
} from '@angular/core';


import DevExpress from 'devextreme/bundles/dx.all';

import DxFilterBuilder from 'devextreme/ui/filter_builder';

import { DxValidatorComponent } from './validator';

import {
    ControlValueAccessor,
    NG_VALUE_ACCESSOR
} from '@angular/forms';

import { DxComponent } from '../core/component';
import { DxTemplateHost } from '../core/template-host';
import { DxTemplateModule } from '../core/template';
import { EventsRegistrator } from '../core/events-strategy';
import { NestedOptionHost } from '../core/nested-option';
import { WatcherHelper } from '../core/watcher-helper';
import { IterableDifferHelper } from '../core/iterable-differ-helper';

import { DxiFieldModule } from './nested/field-dxi';
import { DxoFormatModule } from './nested/format';
import { DxoLookupModule } from './nested/lookup';
import { DxoFilterOperationDescriptionsModule } from './nested/filter-operation-descriptions';
import { DxoGroupOperationDescriptionsModule } from './nested/group-operation-descriptions';

import { DxiFieldComponent } from './nested/field-dxi';




const CUSTOM_VALUE_ACCESSOR_PROVIDER = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DxFilterBuilderComponent),
    multi: true
};

/**
 * The FilterBuilder widget allows a user to build complex filter expressions with an unlimited number of filter conditions, combined by logical operations using the UI.
 */

export class DxFilterBuilderComponent extends DxComponent implements OnDestroy, OnInit, AfterViewInit, ControlValueAccessor, OnChanges, DoCheck {
    instance: DxFilterBuilder;

    
    validator: DxValidatorComponent;


    /**
     * Specifies the shortcut key that sets focus on the widget.
     */
    
    get accessKey(): string {
        return this._getOption('accessKey');
    }
    set accessKey(value: string) {
        this._setOption('accessKey', value);
    }


    /**
     * Specifies whether or not the widget changes its state when interacting with a user.
     */
    
    get activeStateEnabled(): boolean {
        return this._getOption('activeStateEnabled');
    }
    set activeStateEnabled(value: boolean) {
        this._setOption('activeStateEnabled', value);
    }


    /**
     * Specifies whether the widget can display hierarchical data fields.
     */
    
    get allowHierarchicalFields(): boolean {
        return this._getOption('allowHierarchicalFields');
    }
    set allowHierarchicalFields(value: boolean) {
        this._setOption('allowHierarchicalFields', value);
    }


    /**
     * Specifies whether the widget responds to user interaction.
     */
    
    get disabled(): boolean {
        return this._getOption('disabled');
    }
    set disabled(value: boolean) {
        this._setOption('disabled', value);
    }


    /**
     * Specifies the attributes to be attached to the widget's root element.
     */
    
    get elementAttr(): any {
        return this._getOption('elementAttr');
    }
    set elementAttr(value: any) {
        this._setOption('elementAttr', value);
    }


    /**
     * Configures fields.
     */
    
    get fields(): Array<DevExpress.ui.dxFilterBuilderField> {
        return this._getOption('fields');
    }
    set fields(value: Array<DevExpress.ui.dxFilterBuilderField>) {
        this._setOption('fields', value);
    }


    /**
     * Specifies filter operation descriptions.
     */
    
    get filterOperationDescriptions(): any {
        return this._getOption('filterOperationDescriptions');
    }
    set filterOperationDescriptions(value: any) {
        this._setOption('filterOperationDescriptions', value);
    }


    /**
     * Specifies whether the widget can be focused using keyboard navigation.
     */
    
    get focusStateEnabled(): boolean {
        return this._getOption('focusStateEnabled');
    }
    set focusStateEnabled(value: boolean) {
        this._setOption('focusStateEnabled', value);
    }


    /**
     * Specifies group operation descriptions.
     */
    
    get groupOperationDescriptions(): any {
        return this._getOption('groupOperationDescriptions');
    }
    set groupOperationDescriptions(value: any) {
        this._setOption('groupOperationDescriptions', value);
    }


    /**
     * Specifies the widget's height.
     */
    
    get height(): number|
        Function|
        string {
        return this._getOption('height');
    }
    set height(value: number|
        Function|
        string) {
        this._setOption('height', value);
    }


    /**
     * Specifies text for a hint that appears when a user pauses on the widget.
     */
    
    get hint(): string {
        return this._getOption('hint');
    }
    set hint(value: string) {
        this._setOption('hint', value);
    }


    /**
     * Specifies whether the widget changes its state when a user pauses on it.
     */
    
    get hoverStateEnabled(): boolean {
        return this._getOption('hoverStateEnabled');
    }
    set hoverStateEnabled(value: boolean) {
        this._setOption('hoverStateEnabled', value);
    }


    /**
     * Switches the widget to a right-to-left representation.
     */
    
    get rtlEnabled(): boolean {
        return this._getOption('rtlEnabled');
    }
    set rtlEnabled(value: boolean) {
        this._setOption('rtlEnabled', value);
    }


    /**
     * Specifies the number of the element when the Tab key is used for navigating.
     */
    
    get tabIndex(): number {
        return this._getOption('tabIndex');
    }
    set tabIndex(value: number) {
        this._setOption('tabIndex', value);
    }


    /**
     * Specifies the current filter expression.
     */
    
    get value(): any {
        return this._getOption('value');
    }
    set value(value: any) {
        this._setOption('value', value);
    }


    /**
     * Specifies whether the widget is visible.
     */
    
    get visible(): boolean {
        return this._getOption('visible');
    }
    set visible(value: boolean) {
        this._setOption('visible', value);
    }


    /**
     * Specifies the widget's width.
     */
    
    get width(): number|
        Function|
        string {
        return this._getOption('width');
    }
    set width(value: number|
        Function|
        string) {
        this._setOption('width', value);
    }

    /**
     * A handler for the disposing event. Executed when the widget is removed from the DOM using the remove(), empty(), or html() jQuery methods only.
     */
     onDisposing: EventEmitter<any>;

    /**
     * A handler for the editorPrepared event. Executed after an editor is created.
     */
     onEditorPrepared: EventEmitter<any>;

    /**
     * A handler for the editorPreparing event. Executed before an editor is created.
     */
     onEditorPreparing: EventEmitter<any>;

    /**
     * A handler for the initialized event. Executed only once, after the widget is initialized.
     */
     onInitialized: EventEmitter<any>;

    /**
     * A handler for the optionChanged event. Executed after an option of the widget is changed.
     */
     onOptionChanged: EventEmitter<any>;

    /**
     * A handler for the valueChanged event. Executed after the widget's value is changed.
     */
     onValueChanged: EventEmitter<any>;

    /**
     * A handler for the accessKeyChange event.
     */
     accessKeyChange: EventEmitter<string>;

    /**
     * A handler for the activeStateEnabledChange event.
     */
     activeStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the allowHierarchicalFieldsChange event.
     */
     allowHierarchicalFieldsChange: EventEmitter<boolean>;

    /**
     * A handler for the disabledChange event.
     */
     disabledChange: EventEmitter<boolean>;

    /**
     * A handler for the elementAttrChange event.
     */
     elementAttrChange: EventEmitter<any>;

    /**
     * A handler for the fieldsChange event.
     */
     fieldsChange: EventEmitter<Array<DevExpress.ui.dxFilterBuilderField>>;

    /**
     * A handler for the filterOperationDescriptionsChange event.
     */
     filterOperationDescriptionsChange: EventEmitter<any>;

    /**
     * A handler for the focusStateEnabledChange event.
     */
     focusStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the groupOperationDescriptionsChange event.
     */
     groupOperationDescriptionsChange: EventEmitter<any>;

    /**
     * A handler for the heightChange event.
     */
     heightChange: EventEmitter<number|
        Function|
        string>;

    /**
     * A handler for the hintChange event.
     */
     hintChange: EventEmitter<string>;

    /**
     * A handler for the hoverStateEnabledChange event.
     */
     hoverStateEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the rtlEnabledChange event.
     */
     rtlEnabledChange: EventEmitter<boolean>;

    /**
     * A handler for the tabIndexChange event.
     */
     tabIndexChange: EventEmitter<number>;

    /**
     * A handler for the valueChange event.
     */
     valueChange: EventEmitter<any>;

    /**
     * A handler for the visibleChange event.
     */
     visibleChange: EventEmitter<boolean>;

    /**
     * A handler for the widthChange event.
     */
     widthChange: EventEmitter<number|
        Function|
        string>;

     onBlur: EventEmitter<any>;


     change(_) { }
     touched = () => {};


    
    get fieldsChildren(): QueryList<DxiFieldComponent> {
        return this._getOption('fields');
    }
    set fieldsChildren(value) {
        this.setChildren('fields', value);
    }


    constructor(elementRef: ElementRef, ngZone: NgZone, templateHost: DxTemplateHost, injector: Injector,
            private _watcherHelper: WatcherHelper,
            private _idh: IterableDifferHelper, optionHost: NestedOptionHost) {

        super(elementRef, ngZone, templateHost, _watcherHelper);
        injector.get(EventsRegistrator);

        this._createEventEmitters([
            { subscribe: 'disposing', emit: 'onDisposing' },
            { subscribe: 'editorPrepared', emit: 'onEditorPrepared' },
            { subscribe: 'editorPreparing', emit: 'onEditorPreparing' },
            { subscribe: 'initialized', emit: 'onInitialized' },
            { subscribe: 'optionChanged', emit: 'onOptionChanged' },
            { subscribe: 'valueChanged', emit: 'onValueChanged' },
            { emit: 'accessKeyChange' },
            { emit: 'activeStateEnabledChange' },
            { emit: 'allowHierarchicalFieldsChange' },
            { emit: 'disabledChange' },
            { emit: 'elementAttrChange' },
            { emit: 'fieldsChange' },
            { emit: 'filterOperationDescriptionsChange' },
            { emit: 'focusStateEnabledChange' },
            { emit: 'groupOperationDescriptionsChange' },
            { emit: 'heightChange' },
            { emit: 'hintChange' },
            { emit: 'hoverStateEnabledChange' },
            { emit: 'rtlEnabledChange' },
            { emit: 'tabIndexChange' },
            { emit: 'valueChange' },
            { emit: 'visibleChange' },
            { emit: 'widthChange' },
            { emit: 'onBlur' }
        ]);

        this._idh.setHost(this);
        optionHost.setHost(this);
    }

    protected _createInstance(element, options) {
        return new DxFilterBuilder(element, options);
    }

    writeValue(value: any): void {
        this.value = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    registerOnChange(fn: (_: any) => void): void { this.change = fn; }
    registerOnTouched(fn: () => void): void { this.touched = fn; }

    _createWidget(element: any) {
        super._createWidget(element);
        this.instance.on('focusOut', (e) => {
            this.eventHelper.fireNgEvent('onBlur', [e]);
        });
    }

    ngOnDestroy() {
        this._destroyWidget();
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        this.setupChanges('fields', changes);
    }

    setupChanges(prop: string, changes: SimpleChanges) {
        if (!(prop in this._optionsToUpdate)) {
            this._idh.setup(prop, changes);
        }
    }

    ngDoCheck() {
        this._idh.doCheck('fields');
        this._watcherHelper.checkWatchers();
        super.ngDoCheck();
    }

    _setOption(name: string, value: any) {
        let isSetup = this._idh.setupSingle(name, value);
        let isChanged = this._idh.getChanges(name, value) !== null;

        if (isSetup || isChanged) {
            super._setOption(name, value);
        }
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.validator) {
            this.validator.createInstanceOnInit = false;
        }
    }
    ngAfterViewInit() {
        super.ngAfterViewInit();
        if (this.validator) {
            this.validator.createInstance(this.element.nativeElement);
        }
    }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
    selector: 'dx-filter-builder',
    template: '',
    providers: [
        DxTemplateHost,
        WatcherHelper,
        CUSTOM_VALUE_ACCESSOR_PROVIDER,
        NestedOptionHost,
        IterableDifferHelper
    ]
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: NgZone, },
{type: DxTemplateHost, },
{type: Injector, },
{type: WatcherHelper, },
{type: IterableDifferHelper, },
{type: NestedOptionHost, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'validator': [{ type: ContentChild, args: [DxValidatorComponent, ] },],
'accessKey': [{ type: Input },],
'activeStateEnabled': [{ type: Input },],
'allowHierarchicalFields': [{ type: Input },],
'disabled': [{ type: Input },],
'elementAttr': [{ type: Input },],
'fields': [{ type: Input },],
'filterOperationDescriptions': [{ type: Input },],
'focusStateEnabled': [{ type: Input },],
'groupOperationDescriptions': [{ type: Input },],
'height': [{ type: Input },],
'hint': [{ type: Input },],
'hoverStateEnabled': [{ type: Input },],
'rtlEnabled': [{ type: Input },],
'tabIndex': [{ type: Input },],
'value': [{ type: Input },],
'visible': [{ type: Input },],
'width': [{ type: Input },],
'onDisposing': [{ type: Output },],
'onEditorPrepared': [{ type: Output },],
'onEditorPreparing': [{ type: Output },],
'onInitialized': [{ type: Output },],
'onOptionChanged': [{ type: Output },],
'onValueChanged': [{ type: Output },],
'accessKeyChange': [{ type: Output },],
'activeStateEnabledChange': [{ type: Output },],
'allowHierarchicalFieldsChange': [{ type: Output },],
'disabledChange': [{ type: Output },],
'elementAttrChange': [{ type: Output },],
'fieldsChange': [{ type: Output },],
'filterOperationDescriptionsChange': [{ type: Output },],
'focusStateEnabledChange': [{ type: Output },],
'groupOperationDescriptionsChange': [{ type: Output },],
'heightChange': [{ type: Output },],
'hintChange': [{ type: Output },],
'hoverStateEnabledChange': [{ type: Output },],
'rtlEnabledChange': [{ type: Output },],
'tabIndexChange': [{ type: Output },],
'valueChange': [{ type: Output },],
'visibleChange': [{ type: Output },],
'widthChange': [{ type: Output },],
'onBlur': [{ type: Output },],
'change': [{ type: HostListener, args: ['valueChange', ['$event'], ] },],
'touched': [{ type: HostListener, args: ['onBlur', ['$event'], ] },],
'fieldsChildren': [{ type: ContentChildren, args: [DxiFieldComponent, ] },],
};
}


export class DxFilterBuilderModule { static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  imports: [
    DxiFieldModule,
    DxoFormatModule,
    DxoLookupModule,
    DxoFilterOperationDescriptionsModule,
    DxoGroupOperationDescriptionsModule,
    DxTemplateModule
  ],
  declarations: [
    DxFilterBuilderComponent
  ],
  exports: [
    DxFilterBuilderComponent,
    DxiFieldModule,
    DxoFormatModule,
    DxoLookupModule,
    DxoFilterOperationDescriptionsModule,
    DxoGroupOperationDescriptionsModule,
    DxTemplateModule
  ],
  providers: [EventsRegistrator]

}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
];
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
